import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Minus } from "lucide-react";
import { FadeUp, EASE } from "@/components/Reveal";
import { faqs } from "@/data/site";

const FAQContent = () => {
  const [openIdx, setOpenIdx] = useState(0);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
      <FadeUp className="lg:col-span-5">
        <h2 className="text-4xl sm:text-5xl font-normal tracking-tight">
          Frequently Asked <span className="text-[#8C5EBA] font-medium">Questions</span>
        </h2>
        <p className="mt-6 max-w-sm text-base leading-relaxed text-[#111]/60">
          As a leading digital marketing agency, we are dedicated to providing comprehensive educational resources and answering frequently asked questions to help clients.
        </p>
        <div className="mt-8 flex items-center gap-6">
          <Link to="/contact" data-testid="faq-more-questions-btn" className="btn-pill btn-outline-accent !py-2.5 !px-5 text-[15px] !font-bold">
            More Questions
          </Link>
          <Link to="/contact" data-testid="faq-contact-link" className="underline-link text-[15px]">
            Contact Us
          </Link>
        </div>
      </FadeUp>

      <div className="lg:col-span-7">
        {faqs.map((f, i) => (
          <FadeUp key={i} delay={i * 0.08}>
            <div className="border-t border-[#8C5EBA]/40">
              <button
                data-testid={`faq-question-${i}`}
                className="w-full flex items-start justify-between gap-6 py-6 text-left group"
                onClick={() => setOpenIdx(openIdx === i ? -1 : i)}
              >
                <span className="text-lg sm:text-2xl font-semibold max-w-xl group-hover:text-[#8C5EBA] transition-colors duration-300">
                  {f.q}
                </span>
                <span className="mt-1 text-[#8C5EBA] shrink-0">
                  {openIdx === i ? <Minus size={20} /> : <Plus size={20} />}
                </span>
              </button>
              <AnimatePresence initial={false}>
                {openIdx === i && (
                  <motion.div
                    data-testid={`faq-answer-${i}`}
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.5, ease: EASE }}
                    className="overflow-hidden"
                  >
                    <p className="pb-7 pr-10 text-base sm:text-lg leading-relaxed text-[#111]/60 max-w-2xl">{f.a}</p>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </FadeUp>
        ))}
        <div className="border-t border-[#8C5EBA]/40" />
      </div>
    </div>
  );
};

const FAQSection = ({ boxed = false }) => {
  if (boxed) {
    return (
      <section id="faqs" data-testid="faq-section" className="relative z-10 w-full px-6 py-10">
        <div className="relative bg-white rounded-[32px] py-16 sm:py-24 overflow-hidden" style={{ boxShadow: "0 20px 50px rgba(17,17,17,0.06)" }}>
          <div className="stripes-overlay" aria-hidden="true" />
          <div className="relative site-container">
            <FAQContent />
          </div>
        </div>
      </section>
    );
  }
  return (
    <section id="faqs" data-testid="faq-section" className="relative z-10 site-container py-20 sm:py-28">
      <FAQContent />
    </section>
  );
};

export default FAQSection;
